import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Chip,
  Slider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { FaDownload } from "react-icons/fa";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import "bootstrap/dist/css/bootstrap.min.css";

import {
  fetchRiskPermissions,
  fetchRiskRecords,
  fetchRiskActionCards,
  fetchOrganisationEmployees,
  fetchMissionGuestData,
  fetchEstimatedRescueData,
} from "../store/reducers/riskAnalysisSlice";
import { getOrganizationById } from "../store/reducers/organizationSlice";
import RawRecordsTable from "./components/RawRecordsTable";
import ReportingCategoryTable from "./components/ReportingCategoryTable";
import ReportingCategoryDateChart from "./components/ReportingCategoryDateChart";
import RiskBarChart from "./components/RiskBarChart";
import EstimatedTimeChart from "./components/EstimatedTimeChart";
import MissionChart from "./components/Missionchart";
import GuestChart from "./components/Guestchart";
import TPerformanceChart from "./components/TPerformanceChart";
import ChartImageExportCard from "./components/ChartImageExportCard";
import {
  getLocalDateOrFallback,
  getLocalHourOrFallback,
} from "./utils/localDateTime";

const MIN_DATE = "2017-01-01";
const todayStr = () => new Date().toISOString().slice(0, 10);
const threeMonthsAgoStr = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 3);
  return date.toISOString().slice(0, 10);
};
const weekAgoStr = () =>
  new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

const T = {
  en: {
    timePeriod: "Select Time Period",
    setNumber: "Set number",
    employee: "Select Employee",
    timeOfDay: "Select time of day",
    training: "View training data",
    read: "View reading data",
    see_null_data:"View Null data for Training and Read",
    incidentType: "Real incident type",
    yellow: "Yellow",
    red: "Red",
    reportingCategory: "Reporting category",
    nearMiss: "Near miss",
    prevention: "Prevention",
    doubtAboutReporting: "Doubt about reporting",
    faktuel: "Real Incident",
    performance: "T-performance",
    mission: "Mission",
    guest: "Guest",
    estimated: "Estimated rescue time",
    downloadPdf: "Risk analysis report (pdf)",
    downloadPng: "Risk picture (png)",
    downloadCsv: "Download CSV",
    clearFilters: "Clear filters",
    showing: "Showing",
    of: "of",
    records: "records",
    rawRecords: "Raw Records",
    beaches: "Beaches",
  },
  dk: {
    timePeriod: "Vælg tidsperiode",
    setNumber: "Sætnummer",
    employee: "Vælg medarbejder",
    timeOfDay: "Vælg tidspunkt på døgnet",
    training: "Vis træningsdata",
    read: "Vis læsningsdata",
    see_null_data:"Se Null data for Træning og Læst",
    incidentType: "Faktisk hændelsestype",
    yellow: "Gul",
    red: "Rød",
    reportingCategory: "Rapporteringskategori",
    nearMiss: "Near miss",
    prevention: "Forebyggelse",
    doubtAboutReporting: "Tvivl om rapportering",
    faktuel: "Faktisk hændelse",
    performance: "T-performance",
    mission: "Mission",
    guest: "Gæst",
    estimated: "Estimated rescue time",
    downloadPdf: "Risiko rapport (pdf)",
    downloadPng: "Risiko billede (png)",
    downloadCsv: "Download CSV",
    showing: "Viser",
    of: "af",
    records: "poster",
    rawRecords: "Rådata",
    beaches: "Vælg beach",
  },
};

const MultiSelect = ({ label, options, value, onChange, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <TextField
      select
      label={label}
      SelectProps={{
        multiple: true,
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
        renderValue: (selected) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {selected.map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onDelete={() => onDelete(v)}
              />
            ))}
          </Box>
        ),
      }}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        setOpen(false); // auto-close after a selection
      }}
      fullWidth
      size="small"
      sx={{ mt: 1 }}
    >
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt}
        </MenuItem>
      ))}
    </TextField>
  );
};

const MISSION_GUEST_ORGS = [19, 2];
const ESTIMATED_ORGS = [19];

const showMissionGuest = (orgId) => MISSION_GUEST_ORGS.includes(orgId);
const showEstimated = (orgId) => ESTIMATED_ORGS.includes(orgId);

const PDF_TABLE_HEADERS = {
  en: [
    { key: "date", label: "Date" },
    { key: "time", label: "Time" },
    { key: "set", label: "Set no." },
    { key: "card", label: "Actioncard name" },
    { key: "comment", label: "Comment" },
    { key: "employee", label: "Employee name" },
  ],
  dk: [
    { key: "date", label: "Dato" },
    { key: "time", label: "Tid" },
    { key: "set", label: "Sæt nr." },
    { key: "card", label: "Actioncard navn" },
    { key: "comment", label: "Kommentar" },
    { key: "employee", label: "Medarbejder navn" },
  ],
};

const normalizePrototypeGroups = (source, listKey) => {
  if (Array.isArray(source)) {
    return [
      {
        key: "default",
        prototype_set_id: "",
        english_set_name: "",
        danish_set_name: "",
        [listKey]: source,
      },
    ];
  }

  if (!source || typeof source !== "object") {
    return [];
  }

  return Object.entries(source).map(([key, value]) => ({
    key,
    prototype_set_id: value?.prototype_set_id ?? key,
    english_set_name: value?.english_set_name ?? "",
    danish_set_name: value?.danish_set_name ?? "",
    [listKey]: Array.isArray(value?.[listKey]) ? value[listKey] : [],
  }));
};

const dedupeBy = (items, getKey) => {
  const seen = new Set();
  const rows = [];

  items.forEach((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push(item);
  });

  return rows;
};

const hasValidEmployeeValue = (employee) => {
  if (employee === null || employee === undefined) return false;
  const normalized = String(employee).trim();
  if (!normalized) return false;
  return normalized.toLowerCase() !== "null";
};

const normalizeIncidentType = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "null" ? "" : normalized;
};

const getIncidentTypeValue = (record) =>
  normalizeIncidentType(record?.real_incident_type || record?.read_incident_type);

const isTruthyFlag = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  return normalized === "1" || normalized === "true";
};

const buildFallbackCardsFromRecords = (records = []) =>
  dedupeBy(
    (Array.isArray(records) ? records : [])
      .map((record) => String(record?.action_card_number ?? "").trim())
      .filter(Boolean)
      .map((acNo) => ({
        ac_no: acNo,
        english_short_name: acNo,
        danish_short_name: acNo,
      })),
    (card) => String(card?.ac_no ?? "")
  );

const RiskAnalysis = () => {
  const dispatch = useDispatch();
  const organizationId = Number(localStorage.getItem("organisation_id"));

  const {
    permissions: {
      data: permissionData,
      loading: permissionsLoading,
      error: permissionsError,
      success: permissionsSuccess,
    },
    riskRecords: {
      data: riskRecordsData,
      loading: riskRecordsLoading,
      error: riskRecordsError,
      success: riskRecordsSuccess,
    },
    actionCards: {
      data: actionCardsData,
      loading: actionCardsLoading,
      error: actionCardsError,
      success: actionCardsSuccess,
    },
    employees: {
      data: employeeData,
      loading: employeesLoading,
      error: employeesError,
      success: employeesSuccess,
    },
    missionGuest: {
      data: missionGuestData,
      loading: missionGuestLoading,
      error: missionGuestError,
      success: missionGuestSuccess,
    },
    estimatedRescue: {
      data: estimatedData,
      loading: estimatedLoading,
      error: estimatedError,
      success: estimatedSuccess,
    },
  } = useSelector((state) => state.riskAnalysis);

  const {
    organizationDetails,
    detailsLoading: orgLoading,
    detailsError: orgError,
  } = useSelector((state) => state.organization);

  const orgDetails = useMemo(
    () => (Array.isArray(organizationDetails) ? organizationDetails[0] : organizationDetails),
    [organizationDetails]
  );

  const [lang, setLang] = useState("dk");
  const [tab, setTab] = useState(0);
  const [timeRange, setTimeRange] = useState([0, 24]);
  const [dateFrom, setDateFrom] = useState(MIN_DATE);
  const [dateTo, setDateTo] = useState(todayStr());
  const [employees, setEmployees] = useState([]);
  const [setNumbers, setSetNumbers] = useState([]);
  const [showTraining, setShowTraining] = useState(false);
  const [showRead, setShowRead] = useState(false);
  const [showNull, setShowNull] = useState(false);
  const [showIncidentYellow, setShowIncidentYellow] = useState(false);
  const [showIncidentRed, setShowIncidentRed] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pngDownloading, setPngDownloading] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);

  // Reporting category filters
  const [rDateFrom, setRDateFrom] = useState(threeMonthsAgoStr());
  const [rDateTo, setRDateTo] = useState(todayStr());
  const [rTimeRange, setRTimeRange] = useState([0, 24]);
  const [rEmployees, setREmployees] = useState([]);
  const [rSetNumbers, setRSetNumbers] = useState([]);
  const [rShowNearMiss, setRShowNearMiss] = useState(true);
  const [rShowPrevention, setRShowPrevention] = useState(true);
  const [rShowDoubtAboutReporting, setRShowDoubtAboutReporting] = useState(true);

  // Mission filters
  const [mDateFrom, setMDateFrom] = useState(weekAgoStr());
  const [mDateTo, setMDateTo] = useState(todayStr());
  const [mTimeRange, setMTimeRange] = useState([0, 24]);
  const [mSetNumbers, setMSetNumbers] = useState([]);
  const [mShowGreen, setMShowGreen] = useState(true);
  const [mShowYellow, setMShowYellow] = useState(true);
  const [mShowRed, setMShowRed] = useState(true);

  // Guest filters
  const [gDateFrom, setGDateFrom] = useState(weekAgoStr());
  const [gDateTo, setGDateTo] = useState(todayStr());
  const [gTimeRange, setGTimeRange] = useState([0, 24]);
  const [gSetNumbers, setGSetNumbers] = useState([]);

  // Estimated filters
  const [eDateFrom, setEDateFrom] = useState(MIN_DATE);
  const [eDateTo, setEDateTo] = useState(todayStr());
  const [eTimeRange, setETimeRange] = useState([0, 24]);
  const [eSetNumbers, setESetNumbers] = useState([]);
  const [eBeach, setEBeach] = useState("");

  const incidentRef = useRef(null);
  const tPerformanceRef = useRef(null);
  const tableRef = useRef(null);
  const missionRef = useRef(null);
  const guestRef = useRef(null);
  const estimatedRef = useRef(null);
  const incidentChartRefs = useRef({});
  const tPerformanceChartRefs = useRef({});
  const incidentImageExportRefs = useRef({});
  const tPerformanceImageExportRefs = useRef({});
  const incidentExportRefs = useRef({});
  const tPerformanceExportRefs = useRef({});
  const missionExportRef = useRef(null);
  const guestExportRef = useRef(null);
  const estimatedExportRef = useRef(null);

  const assignNodeRef = useCallback((refStore, key, node) => {
    if (!key) return;
    if (node) {
      refStore.current[key] = node;
      return;
    }
    delete refStore.current[key];
  }, []);

  useEffect(() => {
    if (!organizationId) return;
    dispatch(fetchRiskPermissions(organizationId));
    dispatch(fetchRiskRecords(organizationId));
    dispatch(fetchRiskActionCards(organizationId));
    dispatch(fetchOrganisationEmployees(organizationId));
    dispatch(getOrganizationById(organizationId));
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (!organizationId || !showMissionGuest(organizationId)) return;
    dispatch(
      fetchMissionGuestData({
        organizationId,
        startDate: mDateFrom || weekAgoStr(),
        endDate: mDateTo || todayStr(),
      })
    );
  }, [dispatch, organizationId, mDateFrom, mDateTo]);

  // Guest filters should also trigger a refresh
  useEffect(() => {
    if (!organizationId || !showMissionGuest(organizationId)) return;
    dispatch(
      fetchMissionGuestData({
        organizationId,
        startDate: gDateFrom || weekAgoStr(),
        endDate: gDateTo || todayStr(),
      })
    );
  }, [dispatch, organizationId, gDateFrom, gDateTo]);

  useEffect(() => {
    if (!showEstimated(organizationId)) return;
    if (organizationId === 19) {
      dispatch(fetchEstimatedRescueData(organizationId));
    }
  }, [dispatch, organizationId]);

  const prototypeRiskGroups = useMemo(
    () => normalizePrototypeGroups(riskRecordsData, "items"),
    [riskRecordsData]
  );
  const prototypeActionCardGroups = useMemo(
    () => normalizePrototypeGroups(actionCardsData, "cards"),
    [actionCardsData]
  );
  const isPrototypeGrouped = useMemo(
    () =>
      Boolean(
        riskRecordsData &&
          !Array.isArray(riskRecordsData) &&
          typeof riskRecordsData === "object"
      ),
    [riskRecordsData]
  );
  const rawRecords = useMemo(
    () =>
      dedupeBy(
        prototypeRiskGroups.flatMap((group) => group.items ?? []),
        (record) =>
          String(
            record?.id ??
              `${record?.utc_tid_skannet ?? ""}-${record?.action_card_number ?? ""}-${record?.user_id ?? ""}`
          )
      ),
    [prototypeRiskGroups]
  );
  const actionCards = useMemo(
    () => {
      const deduped = dedupeBy(
        prototypeActionCardGroups.flatMap((group) => group.cards ?? []),
        (card) => String(card?.ac_no ?? card?.id ?? "")
      );
      return deduped.length ? deduped : buildFallbackCardsFromRecords(rawRecords);
    },
    [prototypeActionCardGroups, rawRecords]
  );
  const prototypeActionCardMap = useMemo(() => {
    const map = {};
    prototypeActionCardGroups.forEach((group) => {
      const cards = Array.isArray(group.cards) ? group.cards : [];
      if (group.key) {
        map[group.key] = cards;
      }
      if (group.prototype_set_id) {
        map[group.prototype_set_id] = cards;
      }
    });
    return map;
  }, [prototypeActionCardGroups]);
  const employeeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(employeeData) ? employeeData : [])
            .map((employee) => employee?.employee_id)
            .filter(Boolean)
        )
      ).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })),
    [employeeData]
  );
  const selectedEmployeeUserIds = useMemo(
    () =>
      (Array.isArray(employeeData) ? employeeData : [])
        .filter((employee) => employees.includes(employee?.employee_id))
        .map((employee) => String(employee?.user_id ?? "")),
    [employeeData, employees]
  );
  const realIncidentSetOptions = useMemo(
    () => {
      const uniq = new Set(
        rawRecords.map((record) => String(record?.nb_set ?? "")).filter((value) => value.trim() !== "")
      );
      return Array.from(uniq).sort((a, b) => Number(a) - Number(b));
    },
    [rawRecords]
  );

  const permissions = permissionData?.permissions_list ?? [];
  const userRole = (permissionData?.user_role ?? "").toLowerCase();
  const currentUserId = String(permissionData?.user_id ?? "");
  const canDate = permissions.includes("use_date_filter");
  const canTime = permissions.includes("use_time_a_day_filter");
  const canSet = permissions.includes("use_set_number_filter");
  const canEmployee = permissions.includes("use_employee_name_filter");
  const canDownload = permissions.includes("download_csv");
  const showReportingCategory = Boolean(orgDetails?.show_reporting_category);

  const applyBaseFilter = useCallback(
    (pool) =>
      pool.filter((rec) => {
        const recDate = getLocalDateOrFallback(rec.utc_tid_skannet);
        if (dateFrom && recDate < dateFrom) return false;
        if (dateTo && recDate > dateTo) return false;
        if (
          employees.length > 0 &&
          !employees.includes(rec.employee) &&
          !selectedEmployeeUserIds.includes(String(rec.user_id ?? ""))
        )
          return false;
        if (setNumbers.length > 0 && !setNumbers.includes(String(rec.nb_set)))
          return false;
        const hour = getLocalHourOrFallback(rec.utc_tid_skannet);
        if (hour !== null && (hour < timeRange[0] || hour > timeRange[1])) return false;
        return true;
      }),
    [dateFrom, dateTo, employees, selectedEmployeeUserIds, setNumbers, timeRange]
  );

  const applyPerformanceFilter = useCallback(
    (pool) =>
      pool.filter((rec) => {
        const recDate = getLocalDateOrFallback(rec.utc_tid_skannet);
        if (dateFrom && recDate < dateFrom) return false;
        if (dateTo && recDate > dateTo) return false;
        const hour = getLocalHourOrFallback(rec.utc_tid_skannet);
        if (hour !== null && (hour < timeRange[0] || hour > timeRange[1])) return false;
        return true;
      }),
    [dateFrom, dateTo, timeRange]
  );

  const applyReportingBaseFilter = useCallback(
    (pool) =>
      pool.filter((rec) => {
        const recDate = getLocalDateOrFallback(rec.utc_tid_skannet);
        if (rDateFrom && recDate < rDateFrom) return false;
        if (rDateTo && recDate > rDateTo) return false;
        if (
          rEmployees.length > 0 &&
          !rEmployees.includes(rec.employee) &&
          !selectedEmployeeUserIds.includes(String(rec.user_id ?? ""))
        ) {
          return false;
        }
        if (rSetNumbers.length > 0 && !rSetNumbers.includes(String(rec.nb_set))) {
          return false;
        }
        const hour = getLocalHourOrFallback(rec.utc_tid_skannet);
        if (hour !== null && (hour < rTimeRange[0] || hour > rTimeRange[1])) return false;
        return true;
      }),
    [rDateFrom, rDateTo, rEmployees, selectedEmployeeUserIds, rSetNumbers, rTimeRange]
  );

  const getPrototypeTitle = useCallback(
    (group) => {
      if (!group) return "";
      const setName =
        lang === "dk"
          ? group.danish_set_name || group.english_set_name
          : group.english_set_name || group.danish_set_name;
      const prototypeId = group.prototype_set_id || group.key || "";
      return [prototypeId, setName].filter(Boolean).join(" - ");
    },
    [lang]
  );
  const sanitizeFilePart = useCallback((value) => {
    const normalized = String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return normalized || "default";
  }, []);
  
  const buildImageFileName = useCallback(
    (prefix, group) => {
      if (!isPrototypeGrouped) return `${prefix}.png`;
      const suffix = sanitizeFilePart(
        group?.prototype_set_id || group?.key || group?.title || "default"
      );
      return `${prefix}-${suffix}.png`;
    },
    [isPrototypeGrouped, sanitizeFilePart]
  );

  const filterNullEmployeeRows = useCallback(
    (records) => (showNull ? records : records.filter((record) => hasValidEmployeeValue(record?.employee))),
    [showNull]
  );

  const isDefaultRecord = useCallback(
    (record) =>
      Number(record?.training_purpose ?? 0) === 1 &&
      Number(record?.has_read ?? 0) === 0,
    []
  );

  const isTrainingRecord = useCallback(
    (record) =>
      Number(record?.training_purpose ?? 0) === 0 &&
      Number(record?.has_read ?? 0) === 0,
    []
  );

  const isReadRecord = useCallback(
    (record) =>
      Number(record?.training_purpose ?? 0) === 1 &&
      Number(record?.has_read ?? 0) === 1,
    []
  );

  const hasReportingCategoryFlag = useCallback(
    (record) =>
      isTruthyFlag(record?.has_never_miss) ||
      isTruthyFlag(record?.has_prevention) ||
      isTruthyFlag(record?.has_doubt_about_reporting),
    []
  );

  const isReportingCategoryRecord = useCallback(
    (record) => isDefaultRecord(record) && hasReportingCategoryFlag(record),
    [hasReportingCategoryFlag, isDefaultRecord]
  );

  const isRealIncidentRecord = useCallback(
    (record) =>
      isDefaultRecord(record) &&
      (!orgDetails?.show_reporting_category || !hasReportingCategoryFlag(record)),
    [hasReportingCategoryFlag, isDefaultRecord, orgDetails]
  );

  const defaultPool = useMemo(
    () =>
      applyBaseFilter(
        rawRecords.filter((record) => isRealIncidentRecord(record))
      ),
    [applyBaseFilter, rawRecords, isRealIncidentRecord]
  );

  const trainingPool = useMemo(
    () =>
      applyBaseFilter(
        filterNullEmployeeRows(
          rawRecords.filter((record) => isTrainingRecord(record))
        )
      ),
    [applyBaseFilter, filterNullEmployeeRows, rawRecords, isTrainingRecord]
  );

  const readPool = useMemo(
    () =>
      applyBaseFilter(
        filterNullEmployeeRows(
          rawRecords.filter((record) => isReadRecord(record))
        )
      ),
    [applyBaseFilter, filterNullEmployeeRows, rawRecords, isReadRecord]
  );

  const prototypeChartGroups = useMemo(
    () =>
      prototypeRiskGroups.map((group) => {
        const groupRecords = Array.isArray(group.items) ? group.items : [];
        const defaultRecords = applyBaseFilter(
          groupRecords.filter((record) => isRealIncidentRecord(record))
        );
        const trainingRecords = applyBaseFilter(
          filterNullEmployeeRows(
            groupRecords.filter((record) => isTrainingRecord(record))
          )
        );
        const readRecords = applyBaseFilter(
          filterNullEmployeeRows(
            groupRecords.filter((record) => isReadRecord(record))
          )
        );
        const performanceRecords = applyPerformanceFilter(
          groupRecords.filter((record) => {
            const yes = Number(record.num_yes ?? 0);
            const no = Number(record.num_no ?? 0);
            const na = Number(record.num_na ?? 0);
            return (
              isTrainingRecord(record) &&
              yes > -1 &&
              no > -1 &&
              na > -1
            );
          })
        );
        const mappedCards =
          prototypeActionCardMap[group.key] ||
          prototypeActionCardMap[group.prototype_set_id] ||
          [];
        const groupActionCards = dedupeBy(
          mappedCards.length ? mappedCards : buildFallbackCardsFromRecords(groupRecords),
          (card) => String(card?.ac_no ?? card?.id ?? "")
        );

        return {
          ...group,
          title: getPrototypeTitle(group),
          actionCards: groupActionCards,
          defaultRecords,
          trainingRecords,
          readRecords,
          performanceRecords,
        };
      }),
    [
      actionCards,
      applyBaseFilter,
      applyPerformanceFilter,
      filterNullEmployeeRows,
      getPrototypeTitle,
      isRealIncidentRecord,
      isReadRecord,
      isTrainingRecord,
      prototypeActionCardMap,
      prototypeRiskGroups,
    ]
  );

  const tableRecords = useMemo(() => {
    const hasIncidentTypeTableFilter = showIncidentYellow || showIncidentRed;
    const hasSpecialRiskFilter = hasIncidentTypeTableFilter;

    return applyBaseFilter(
      rawRecords.filter((record) => {
        if (isDefaultRecord(record)) {
          if (!hasSpecialRiskFilter) {
            return isRealIncidentRecord(record);
          }

          if (hasIncidentTypeTableFilter && isRealIncidentRecord(record)) {
            const incidentType = getIncidentTypeValue(record);
            if (
              (showIncidentYellow && incidentType === "yellow") ||
              (showIncidentRed && incidentType === "red")
            ) {
              return true;
            }
          }
          return false;
        }

        if (showTraining && isTrainingRecord(record)) {
          return showNull || hasValidEmployeeValue(record?.employee);
        }
        if (showRead && isReadRecord(record)) {
          return showNull || hasValidEmployeeValue(record?.employee);
        }
        return false;
      })
    );
  }, [
    applyBaseFilter,
    rawRecords,
    orgDetails,
    isDefaultRecord,
    isRealIncidentRecord,
    showIncidentYellow,
    showIncidentRed,
    showTraining,
    isTrainingRecord,
    showNull,
    showRead,
    isReadRecord,
  ]);

  const reportingCategorySetOptions = useMemo(() => {
    const uniq = new Set(
      rawRecords
        .filter((record) => isReportingCategoryRecord(record))
        .map((record) => String(record?.nb_set ?? ""))
        .filter((value) => value.trim() !== "")
    );
    return Array.from(uniq).sort((a, b) => Number(a) - Number(b));
  }, [rawRecords, isReportingCategoryRecord]);

  const reportingCategoryBasePool = useMemo(
    () =>
      showReportingCategory
        ? applyReportingBaseFilter(
            rawRecords.filter((record) => isReportingCategoryRecord(record))
          )
        : [],
    [applyReportingBaseFilter, isReportingCategoryRecord, rawRecords, showReportingCategory]
  );

  const reportingTableRecords = useMemo(() => {
    const hasSelectedCategory =
      rShowNearMiss || rShowPrevention || rShowDoubtAboutReporting;

    if (!hasSelectedCategory) {
      return [];
    }

    return reportingCategoryBasePool.filter(
      (record) =>
        (rShowNearMiss && isTruthyFlag(record?.has_never_miss)) ||
        (rShowPrevention && isTruthyFlag(record?.has_prevention)) ||
        (rShowDoubtAboutReporting && isTruthyFlag(record?.has_doubt_about_reporting))
    );
  }, [
    reportingCategoryBasePool,
    rShowNearMiss,
    rShowPrevention,
    rShowDoubtAboutReporting,
  ]);

  const reportingPrototypeGroups = useMemo(
    () =>
      prototypeRiskGroups.map((group) => {
        const groupRecords = Array.isArray(group.items) ? group.items : [];
        const reportingCategoryRecords = showReportingCategory
          ? applyReportingBaseFilter(
              groupRecords.filter((record) => isReportingCategoryRecord(record))
            )
          : [];
        const mappedCards =
          prototypeActionCardMap[group.key] ||
          prototypeActionCardMap[group.prototype_set_id] ||
          [];
        const groupActionCards = dedupeBy(
          mappedCards.length ? mappedCards : buildFallbackCardsFromRecords(groupRecords),
          (card) => String(card?.ac_no ?? card?.id ?? "")
        );

        return {
          ...group,
          title: getPrototypeTitle(group),
          actionCards: groupActionCards,
          reportingCategoryRecords,
        };
      }),
    [
      applyReportingBaseFilter,
      getPrototypeTitle,
      isReportingCategoryRecord,
      prototypeActionCardMap,
      prototypeRiskGroups,
      showReportingCategory,
    ]
  );

  // Per-dataset set options
  const missionSetOptions = useMemo(() => {
    const source = missionGuestData?.length ? missionGuestData : [];
    const uniq = new Set(
      source.map((r) => String(r.nb_set ?? "")).filter((v) => v.trim() !== "")
    );
    return Array.from(uniq).sort((a, b) => Number(a) - Number(b));
  }, [missionGuestData]);

  const estimatedSetOptions = useMemo(() => {
    const source = estimatedData?.length ? estimatedData : [];
    const uniq = new Set(
      source.map((r) => String(r.nb_set ?? "")).filter((v) => v.trim() !== "")
    );
    return Array.from(uniq).sort((a, b) => Number(a) - Number(b));
  }, [estimatedData]);

  const missionFiltered = useMemo(
    () =>
      (missionGuestData?.length ? missionGuestData : []).filter(
        (r) => {
          const d = getLocalDateOrFallback(r.utc_scanned);
          if (mDateFrom && d < mDateFrom) return false;
          if (mDateTo && d > mDateTo) return false;
          if (mSetNumbers.length > 0 && !mSetNumbers.includes(String(r.nb_set)))
            return false;
          const hour = getLocalHourOrFallback(r.utc_scanned);
          if (hour !== null && (hour < mTimeRange[0] || hour > mTimeRange[1])) return false;
          return true;
        }
      ),
    [missionGuestData, mDateFrom, mDateTo, mSetNumbers, mTimeRange]
  );

  const guestFiltered = useMemo(
    () =>
      (missionGuestData?.length ? missionGuestData : []).filter(
        (r) => {
          const d = getLocalDateOrFallback(r.utc_scanned);
          if (gDateFrom && d < gDateFrom) return false;
          if (gDateTo && d > gDateTo) return false;
          if (gSetNumbers.length > 0 && !gSetNumbers.includes(String(r.nb_set)))
            return false;
          const hour = getLocalHourOrFallback(r.utc_scanned);
          if (hour !== null && (hour < gTimeRange[0] || hour > gTimeRange[1])) return false;
          return true;
        }
      ),
    [missionGuestData, gDateFrom, gDateTo, gSetNumbers, gTimeRange]
  );

  const estimatedFiltered = useMemo(
    () =>
      (estimatedData?.length ? estimatedData : []).filter((r) => {
        const d = getLocalDateOrFallback(r.utc_scanned, r.date);
        if (eDateFrom && d < eDateFrom) return false;
        if (eDateTo && d > eDateTo) return false;
        if (eSetNumbers.length > 0 && !eSetNumbers.includes(String(r.nb_set)))
          return false;
        if (eBeach && r.beach !== eBeach) return false;
        const hour = getLocalHourOrFallback(r.utc_scanned);
        if (hour !== null && (hour < eTimeRange[0] || hour > eTimeRange[1])) return false;
        return true;
      }),
    [estimatedData, eDateFrom, eDateTo, eSetNumbers, eBeach, eTimeRange]
  );

  const t = (k) => T[lang][k];
  const isGentofteLife = organizationId === 19;
  const hasIncidentTypeFilter = showIncidentYellow || showIncidentRed;

  const orgName =
    (orgDetails && orgDetails.name) ||
    "Organisation Name";

  const captureNodeAsPng = async (node) => {
    if (!node) throw new Error("Nothing to capture");
    return await toPng(node, {
      cacheBust: true,
      pixelRatio: 2,
      useCORS: true,
      skipFonts: true,
    });
  };

  const formatLongDate = (dateStr) => {
    if (!dateStr) return "All";
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric" });
  };

  const formatFilterValue = useCallback(
    (values) => (Array.isArray(values) && values.length ? values.join(", ") : "All"),
    []
  );

  const buildImageHeaderRows = useCallback(
    () => [
      { label: "DOWNLOAD DATE", value: todayStr() },
      { label: "ORGANIZATION", value: orgName },
    ],
    [orgName]
  );

  const buildRiskImageDetailRows = useCallback(
    () => {
      const rows = [
        { label: "Date span", value: `${dateFrom || MIN_DATE} To ${dateTo || todayStr()}` },
        { label: "Time span", value: `${timeRange[0]} To ${timeRange[1]}` },
        { label: "Set of Action-cards®", value: formatFilterValue(setNumbers) },
        { label: "Employees", value: formatFilterValue(employees) },
      ];

      if (isGentofteLife && hasIncidentTypeFilter) {
        rows.push({ label: "Yellow", value: showIncidentYellow ? "Yes" : "No" });
        rows.push({ label: "Red", value: showIncidentRed ? "Yes" : "No" });
      }

      rows.push({ label: "Training", value: showTraining ? "Yes" : "No" });
      rows.push({ label: "Read", value: showRead ? "Yes" : "No" });
      rows.push({ label: "Null Data Checked", value: showNull ? "Yes" : "No" });
      return rows;
    },
    [
      dateFrom,
      dateTo,
      timeRange,
      formatFilterValue,
      setNumbers,
      employees,
      isGentofteLife,
      showIncidentYellow,
      showIncidentRed,
      showTraining,
      showRead,
      showNull,
    ]
  );

  const buildPerformanceImageDetailRows = useCallback(
    () => [
      { label: "Date span", value: `${dateFrom || MIN_DATE} To ${dateTo || todayStr()}` },
      { label: "Time span", value: `${timeRange[0]} To ${timeRange[1]}` },
      { label: "Set of Action-cards®", value: "All" },
      { label: "Employees", value: "All" },
      { label: "Training", value: "No" },
      { label: "Read", value: "No" },
      { label: "Null Data Checked", value: "No" },
    ],
    [dateFrom, dateTo, timeRange]
  );



  const fetchPngDataUrl = async (path) => {
    const res = await fetch(path, { cache: "no-cache" });
    if (!res.ok) throw new Error(`Failed to load ${path}`);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const getLogoDataUrl = async () => {
    try {
      return await fetchPngDataUrl("/img/ac_black_logo.png");
    } catch (e) {
      console.warn("Primary logo load failed, falling back to default", e);
      return await fetchPngDataUrl("/img/logo.png");
    }
  };

  const placeCornerLogo = (doc, logoDataUrl, options = {}) => {
    if (!logoDataUrl) return;
    const pageWidth = doc.internal.pageSize.getWidth();
    const width = options.width ?? 110;
    const top = options.top ?? 18;
    const fmt = logoDataUrl.startsWith("data:image/svg") ? "SVG" : "PNG";
    const props = doc.getImageProperties(logoDataUrl);
    const height = (props.height * width) / props.width;
    doc.addImage(logoDataUrl, fmt, pageWidth - width - 24, top, width, height);
  };

  const placeCenteredLogo = (doc, logoDataUrl, startY = 84, width = 210) => {
    if (!logoDataUrl) return startY;
    const pageWidth = doc.internal.pageSize.getWidth();
    const props = doc.getImageProperties(logoDataUrl);
    const logoHeight = (props.height * width) / props.width;
    const fmt = logoDataUrl.startsWith("data:image/svg") ? "SVG" : "PNG";
    doc.addImage(logoDataUrl, fmt, (pageWidth - width) / 2, startY, width, logoHeight);
    return startY + logoHeight;
  };

  const fitBox = (sourceWidth, sourceHeight, maxWidth, maxHeight) => {
    if (!sourceWidth || !sourceHeight) {
      return { width: maxWidth, height: maxHeight };
    }

    const ratio = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
    return {
      width: sourceWidth * ratio,
      height: sourceHeight * ratio,
    };
  };

  const drawPageChrome = (doc, logoDataUrl, pageNumber, totalPages) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerLabel = lang === "dk" ? "Side" : "Page";

    placeCornerLogo(doc, logoDataUrl);
    doc.setDrawColor(235, 196, 196);
    doc.setLineWidth(0.8);
    doc.line(42, 64, pageWidth - 42, 64);
    doc.line(42, pageHeight - 32, pageWidth - 42, pageHeight - 32);
    doc.setFontSize(9);
    doc.setTextColor(133, 88, 88);
    doc.text(`${footerLabel} ${pageNumber} / ${totalPages}`, pageWidth / 2, pageHeight - 18, {
      align: "center",
    });
  };

  const colorToRgb = (color) => {
    if (Array.isArray(color)) return color;
    const normalized = String(color ?? "").trim();
    if (!normalized.startsWith("#")) return [74, 144, 217];
    const hex = normalized.slice(1);
    const fullHex = hex.length === 3
      ? hex.split("").map((char) => `${char}${char}`).join("")
      : hex;
    return [
      parseInt(fullHex.slice(0, 2), 16),
      parseInt(fullHex.slice(2, 4), 16),
      parseInt(fullHex.slice(4, 6), 16),
    ];
  };

  const drawPdfSummaryRows = (doc, rows, { x, y, width }) => {
    const visibleRows = (rows ?? []).filter((row) => row && row.show !== false);
    if (!visibleRows.length) return;

    const topY = y + 8;
    const gap = 6;
    let cursorX = x + width - 14;

    [...visibleRows].reverse().forEach((row) => {
      const label = `${row.label}: ${row.value}`;
      const badgeWidth = doc.getTextWidth(label) + 28;
      const badgeX = cursorX - badgeWidth;
      const [r, g, b] = colorToRgb(row.color);

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(222, 222, 222);
      doc.roundedRect(badgeX, topY, badgeWidth, 18, 9, 9, "FD");
      doc.setFillColor(r, g, b);
      doc.circle(badgeX + 10, topY + 9, 3.2, "F");
      doc.setFontSize(9);
      doc.setTextColor(65, 65, 65);
      doc.text(label, badgeX + 18, topY + 12);

      cursorX = badgeX - gap;
    });
  };

  const drawChartCard = (doc, { title, dataUrl, summaryRows = [], x, y, width, height, accentColor, borderColor, fillColor }) => {
    const titleSpace = summaryRows.length ? 44 : 28;
    const horizontalPadding = 16;
    const verticalPadding = 10;

    doc.setFillColor(...fillColor);
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(1);
    doc.roundedRect(x, y, width, height, 12, 12, "FD");

    doc.setFontSize(12);
    doc.setTextColor(...accentColor);
    doc.text(title, x + horizontalPadding, y + 20);
    drawPdfSummaryRows(doc, summaryRows, { x, y, width });

    const props = doc.getImageProperties(dataUrl);
    const fitted = fitBox(
      props.width,
      props.height,
      width - horizontalPadding * 2,
      height - titleSpace - verticalPadding * 2
    );
    const imageX = x + (width - fitted.width) / 2;
    const imageY = y + titleSpace + verticalPadding + ((height - titleSpace - verticalPadding * 2 - fitted.height) / 2);
    doc.addImage(dataUrl, "PNG", imageX, imageY, fitted.width, fitted.height);
  };

  const buildRiskPdfSummaryRows = useCallback(
    (defaultRecords, trainingRecords, readRecords) => {
      const rows = [];
      const showIncidentTypeBars =
        isGentofteLife && hasIncidentTypeFilter;
      const hideDefaultBar = showIncidentTypeBars;
      const yellowTotal = defaultRecords.reduce((sum, record) => {
        if (getIncidentTypeValue(record) !== "yellow") {
          return sum;
        }
        const count = Number(record?.real_incident_type_count);
        return sum + (Number.isFinite(count) && count > 0 ? count : 1);
      }, 0);
      const redTotal = defaultRecords.reduce((sum, record) => {
        if (getIncidentTypeValue(record) !== "red") {
          return sum;
        }
        const count = Number(record?.real_incident_type_count);
        return sum + (Number.isFinite(count) && count > 0 ? count : 1);
      }, 0);

      if (showIncidentTypeBars) {
        if (showIncidentYellow) {
          rows.push({
            label: lang === "dk" ? "Gul" : "Yellow",
            value: yellowTotal,
            color: "#e6a817",
            show: true,
          });
        }
        if (showIncidentRed) {
          rows.push({
            label: lang === "dk" ? "Rød" : "Red",
            value: redTotal,
            color: "#c0392b",
            show: true,
          });
        }
      }
      if (!hideDefaultBar) {
        rows.push({
          label: lang === "dk" ? "Faktisk hændelse" : "Real incident",
          value: defaultRecords.length,
          color: "var(--org-bg-color, #f6615f)",
          show: true,
        });
      }
      if (showTraining) {
        rows.push({
          label: lang === "dk" ? "Træning" : "Training",
          value: trainingRecords.length,
          color: "#ff9a98",
          show: true,
        });
      }
      if (showRead) {
        rows.push({
          label: lang === "dk" ? "Læsning" : "Reading",
          value: readRecords.length,
          color: "#4a90d9",
          show: true,
        });
      }
      return rows;
    },
    [
      hasIncidentTypeFilter,
      isGentofteLife,
      lang,
      showIncidentYellow,
      showIncidentRed,
      showRead,
      showTraining,
    ]
  );

  const buildPerformancePdfSummaryRows = useCallback(
    (records, cards) => [
      {
        label: lang === "dk" ? "Kort" : "Cards",
        value: cards.length,
        color: "#4a90d9",
        show: true,
      },
      {
        label: lang === "dk" ? "Træningsrækker" : "Training rows",
        value: records.length,
        color: "#9dc1e8",
        show: true,
      },
    ],
    [lang]
  );

  const handleDownloadPdf = async () => {
    setPdfDownloading(true);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 42;
      const lightRed = [252, 228, 227];
      const softRed = [189, 92, 92];
      const borderRed = [235, 196, 196];
      const bodyFill = [255, 249, 249];
      const logoDataUrl = await getLogoDataUrl();
      const downloadDate = formatLongDate(todayStr());
      const headers = PDF_TABLE_HEADERS[lang] ?? PDF_TABLE_HEADERS.en;
      const contentTop = 82;
      const footerReserve = 48;
      let y = margin;

      y = placeCenteredLogo(doc, logoDataUrl, 108, 220) + 34;
      doc.setFontSize(20);
      doc.setTextColor(88, 34, 34);
      doc.text(lang === "dk" ? "Risikoanalyse rapport" : "Risk Analysis Report", pageWidth / 2, y, {
        align: "center",
      });
      y += 16;

      doc.setFontSize(11);
      doc.setTextColor(120, 64, 64);
      doc.text(orgName, pageWidth / 2, y, { align: "center" });
      y += 30;

      const allLabel = lang === "dk" ? "Alle" : "All";
      const yesLabel = lang === "dk" ? "Ja" : "Yes";
      const noLabel = lang === "dk" ? "Nej" : "No";
      const detailRows = [
        [lang === "dk" ? "Download-dato" : "Download date", downloadDate],
        [lang === "dk" ? "Organisation" : "Organization", orgName],
        [
          lang === "dk" ? "Datointerval" : "Date span",
          `${dateFrom || MIN_DATE} - ${dateTo || todayStr()}`,
        ],
        [lang === "dk" ? "Tidsinterval" : "Time span", `${timeRange[0]} - ${timeRange[1]}`],
        [
          lang === "dk" ? "Sæt" : "Sets",
          setNumbers.length ? setNumbers.join(", ") : allLabel,
        ],
        [
          lang === "dk" ? "Medarbejdere" : "Employees",
          employees.length ? employees.join(", ") : allLabel,
        ],
        [lang === "dk" ? "Træning med" : "Training included", showTraining ? yesLabel : noLabel],
        [lang === "dk" ? "Læsning med" : "Reading included", showRead ? yesLabel : noLabel],
      ];

      doc.setFontSize(13);
      doc.setTextColor(88, 34, 34);
      doc.text(lang === "dk" ? "Rapportdetaljer" : "Report details", margin, y);

      const detailTableStartY = y + 20;
      autoTable(doc, {
        head: [[lang === "dk" ? "Felt" : "Field", lang === "dk" ? "Værdi" : "Value"]],
        body: detailRows,
        startY: detailTableStartY,
        margin: { left: margin, right: margin },
        theme: "grid",
        styles: {
          fontSize: 10,
          cellPadding: 8,
          lineColor: borderRed,
          lineWidth: 0.6,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fillColor: lightRed,
          textColor: softRed,
          fontStyle: "bold",
          lineColor: borderRed,
          lineWidth: 0.6,
        },
        bodyStyles: {
          fillColor: bodyFill,
          textColor: [55, 55, 55],
        },
        columnStyles: {
          0: { cellWidth: 145, fontStyle: "bold", textColor: [104, 48, 48] },
          1: { cellWidth: pageWidth - margin * 2 - 145 },
        },
      });

      const detailFinalY = doc.lastAutoTable?.finalY ?? detailTableStartY + 120;
      doc.setDrawColor(...borderRed);
      doc.setLineWidth(1);
      doc.roundedRect(
        margin,
        detailTableStartY - 12,
        pageWidth - margin * 2,
        detailFinalY - detailTableStartY + 24,
        12,
        12
      );

      if (isPrototypeGrouped) {
        for (const group of prototypeChartGroups) {
          const charts = [];
          const incidentSvg = findSvgInSource(incidentExportRefs.current[group.key]);
          const performanceSvg = findSvgInSource(tPerformanceExportRefs.current[group.key]);

          if (incidentSvg) {
            const { dataUrl } = await svgToPngDataUrl(incidentSvg, 2200);
            charts.push({
              title: lang === "dk" ? "Faktisk hændelse" : "Real Incident",
              dataUrl,
              summaryRows: buildRiskPdfSummaryRows(
                group.defaultRecords,
                group.trainingRecords,
                group.readRecords
              ),
            });
          }

          if (performanceSvg) {
            const { dataUrl } = await svgToPngDataUrl(performanceSvg, 2200);
            charts.push({
              title: "T-Performance",
              dataUrl,
              summaryRows: buildPerformancePdfSummaryRows(
                group.performanceRecords,
                group.actionCards
              ),
            });
          }

          if (!charts.length) continue;

          doc.addPage();
          doc.setFontSize(15);
          doc.setTextColor(88, 34, 34);
          doc.text(group.title || group.prototype_set_id || "", margin, contentTop + 8);

          const cardsTop = contentTop + 16;
          const gap = 10;
          const cardHeight =
            (pageHeight - cardsTop - footerReserve - gap * (charts.length - 1)) /
            charts.length;

          charts.forEach((chart, index) => {
            drawChartCard(doc, {
              title: chart.title,
              dataUrl: chart.dataUrl,
              summaryRows: chart.summaryRows,
              x: margin,
              y: cardsTop + index * (cardHeight + gap),
              width: pageWidth - margin * 2,
              height: cardHeight,
              accentColor: softRed,
              borderColor: borderRed,
              fillColor: bodyFill,
            });
          });
        }
      } else {
        const chartRefs = [
          {
            refs: [incidentExportRefs.current.default, incidentRef],
            title: lang === "dk" ? "Faktisk hændelse" : "Real Incident",
          },
          {
            refs: [tPerformanceExportRefs.current.default, tPerformanceRef],
            title: "T-Performance",
          },
        ];
        const chartExports = [];

        for (const { refs, title } of chartRefs) {
          const svgNode = refs.map((ref) => findSvgInSource(ref)).find(Boolean);
          if (!svgNode) continue;

          const { dataUrl } = await svgToPngDataUrl(svgNode, 2200);
          chartExports.push({
            title,
            dataUrl,
            summaryRows:
              title === "T-Performance"
                ? buildPerformancePdfSummaryRows(
                    prototypeChartGroups[0]?.performanceRecords ?? [],
                    prototypeChartGroups[0]?.actionCards ?? actionCards
                  )
                : buildRiskPdfSummaryRows(
                    defaultPool,
                    trainingPool,
                    readPool
                  ),
          });
        }

        if (chartExports.length) {
          doc.addPage();
          doc.setFontSize(15);
          doc.setTextColor(88, 34, 34);

          const cardsTop = contentTop + 12;
          const gap = 10;
          const cardHeight =
            (pageHeight - cardsTop - footerReserve - gap * (chartExports.length - 1)) /
            chartExports.length;

          chartExports.forEach((chart, index) => {
            drawChartCard(doc, {
              title: chart.title,
              dataUrl: chart.dataUrl,
              summaryRows: chart.summaryRows,
              x: margin,
              y: cardsTop + index * (cardHeight + gap),
              width: pageWidth - margin * 2,
              height: cardHeight,
              accentColor: softRed,
              borderColor: borderRed,
              fillColor: bodyFill,
            });
          });
        }
      }

      doc.addPage();
      y = contentTop;
      doc.setFontSize(15);
      doc.setTextColor(88, 34, 34);
      doc.text(lang === "dk" ? "Rå data" : "Raw records", margin, y);

      const tableRows = displayTableRecords.map(mapRecordForPdf);
      if (tableRows.length) {
        const dataTableStartY = y + 18;
        const tableStartPage = doc.getNumberOfPages();
        autoTable(doc, {
          head: [headers.map((h) => h.label)],
          body: tableRows.map((row) => headers.map((h) => row[h.key])),
          startY: dataTableStartY,
          margin: {
            top: contentTop,
            right: margin,
            bottom: footerReserve,
            left: margin,
          },
          theme: "grid",
          styles: {
            fontSize: 9,
            cellPadding: 6,
            overflow: "linebreak",
            valign: "top",
            lineColor: borderRed,
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: lightRed,
            textColor: softRed,
            fontStyle: "bold",
            lineColor: borderRed,
            lineWidth: 0.5,
          },
          alternateRowStyles: { fillColor: bodyFill },
          bodyStyles: { textColor: [55, 55, 55] },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 48 },
            2: { cellWidth: 45 },
            3: { cellWidth: 108 },
            4: { cellWidth: 170 },
            5: { cellWidth: 80 },
          },
          tableWidth: pageWidth - margin * 2,
        });
        const finalY = doc.lastAutoTable?.finalY ?? dataTableStartY + 40;
        if (doc.getNumberOfPages() === tableStartPage) {
          doc.setDrawColor(...borderRed);
          doc.setLineWidth(1);
          doc.roundedRect(
            margin,
            dataTableStartY - 12,
            pageWidth - margin * 2,
            finalY - dataTableStartY + 24,
            12,
            12
          );
        }
        y = finalY + 12;
      } else {
        doc.setFontSize(12);
        doc.text(lang === "dk" ? "Ingen rækker at eksportere." : "No rows to export.", margin, y + 24);
      }

      const totalPages = doc.getNumberOfPages();
      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
        doc.setPage(pageNumber);
        drawPageChrome(doc, logoDataUrl, pageNumber, totalPages);
      }

      doc.save(`risk-analysis-${orgName || "report"}.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
      alert("Kunne ikke gemme PDF. Prøv igen.");
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleDownloadPng = async () => {
    setPngDownloading(true);
    try {
      const refs = prototypeChartGroups
        .flatMap((group) => [
          {
            node: incidentImageExportRefs.current[group.key],
            name: buildImageFileName("real-incident", group),
          },
          {
            node: tPerformanceImageExportRefs.current[group.key],
            name: buildImageFileName("t-performance", group),
          },
        ])
        .filter((item) => item.node);

      if (!refs.length) throw new Error("No risk or t-performance charts to export.");

      await new Promise((resolve) =>
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(resolve)
        )
      );

      const zip = new JSZip();
      for (const item of refs) {
        const dataUrl = await captureNodeAsPng(item.node);
        zip.file(item.name, dataUrl.split(",")[1], { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `risk-analysis-images.zip`);
    } catch (err) {
      console.error("Image zip export failed", err);
      alert("Kunne ikke gemme billederne. Prøv igen.");
    } finally {
      setPngDownloading(false);
    }
  };

  const renderLoader = (label) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
      <CircularProgress size={18} />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  const displayTableRecords =
    userRole === "employee" || userRole === "employe"
      ? tableRecords.filter((r) => String(r.user_id ?? "") === currentUserId)
      : tableRecords;

  const displayReportingTableRecords =
    userRole === "employee" || userRole === "employe"
      ? reportingTableRecords.filter((r) => String(r.user_id ?? "") === currentUserId)
      : reportingTableRecords;

  const isRiskDownloadBusy = pdfDownloading || pngDownloading || csvDownloading;

  const actionCardLookup = useMemo(() => {
    const map = {};
    actionCards.forEach((card) => {
      map[String(card.ac_no)] = {
        en: card.english_short_name,
        dk: card.danish_short_name,
      };
    });
    return map;
  }, [actionCards]);

  const mapRecordForPdf = useCallback(
    (rec) => {
      const [datePart, timePart] = (rec.utc_tid_skannet ?? "").split(" ");
      const lookup = actionCardLookup[String(rec.action_card_number)] ?? {};
      const cardLabel = lookup[lang] ?? rec.action_card_number ?? "-";

      return {
        date: datePart || "-",
        time: timePart || "-",
        set: rec.nb_set ?? "-",
        card: cardLabel,
        comment: rec.comment || "—",
        employee: rec.employee ?? "-",
      };
    },
    [actionCardLookup, lang]
  );

  const findSvgInSource = useCallback((source) => {
    const node = source?.current ?? source;
    if (!node) return null;
    if (typeof node.querySelector === "function") {
      return node.querySelector("svg");
    }
    if (node?.tagName?.toLowerCase?.() === "svg") {
      return node;
    }
    return null;
  }, []);

  const svgToPngDataUrl = useCallback((svgNode, targetWidth) => {
    return new Promise((resolve, reject) => {
      if (!svgNode) {
        reject(new Error("Missing SVG element"));
        return;
      }

      const clonedSvg = svgNode.cloneNode(true);
      const viewBox = clonedSvg.getAttribute("viewBox");
      const [, , vbWidth, vbHeight] = viewBox
        ? viewBox.split(" ").map(Number)
        : [];

      const naturalWidth = vbWidth || svgNode.getBoundingClientRect().width || targetWidth;
      const naturalHeight = vbHeight || svgNode.getBoundingClientRect().height || targetWidth * 0.6;
      const width = targetWidth;
      const height = naturalWidth ? (naturalHeight * width) / naturalWidth : targetWidth * 0.6;

      const svgString = new XMLSerializer().serializeToString(clonedSvg);
      const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        resolve({ dataUrl: canvas.toDataURL("image/png"), width, height });
      };

      img.onerror = (err) => reject(err);
      img.src = url;
    });
  }, []);

  const downloadCsv = (rows, filename) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const lines = [
      headers.join(","),
      ...rows.map((r) =>
        headers
          .map((h) => {
            const v = r[h] ?? "";
            const s = String(v).replace(/\"/g, '\"\"');
            return s.includes(",") || s.includes("\n") ? `"${s}"` : s;
          })
          .join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadRiskCsv = async () => {
    setCsvDownloading(true);
    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      downloadCsv(displayTableRecords, "risk_analysis.csv");
    } finally {
      setCsvDownloading(false);
    }
  };

  const handleDownloadReportingCsv = async () => {
    setCsvDownloading(true);
    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      downloadCsv(displayReportingTableRecords, "reporting_category.csv");
    } finally {
      setCsvDownloading(false);
    }
  };

  const renderFiltersForTab = () => {
    const sliderMarks = Array.from({ length: 9 }, (_, i) => ({
      value: i * 3,
      label: String(i * 3),
    }));

    const activeTabKey = tabDefs[tab]?.key;

    if (activeTabKey === "faktuel") {
      return (
        <>
          <div className="row mt-4">
            {canDate && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("timePeriod")}
                </Typography>
                <div className="d-flex gap-2">
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                </div>
              </div>
            )}

            {canSet && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("setNumber")}
                </Typography>
                <MultiSelect
                  label=""
                  options={realIncidentSetOptions}
                  value={setNumbers}
                  onChange={(sel) => setSetNumbers(sel)}
                  onDelete={(v) =>
                    setSetNumbers((p) => p.filter((x) => x !== v))
                  }
                />
              </div>
            )}

       
              <div className="col-12 col-lg-4 d-flex flex-column gap-2 mt-1">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={pdfDownloading ? <CircularProgress size={14} color="inherit" /> : <FaDownload />}
                  onClick={handleDownloadPdf}
                  disabled={isRiskDownloadBusy}
                >
                  {t("downloadPdf")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={pngDownloading ? <CircularProgress size={14} color="inherit" /> : <FaDownload />}
                  onClick={handleDownloadPng}
                  disabled={isRiskDownloadBusy}
                >
                  {t("downloadPng")}
                </Button>
                {canDownload && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={csvDownloading ? <CircularProgress size={14} color="inherit" /> : <FaDownload />}
                  onClick={handleDownloadRiskCsv}
                  disabled={isRiskDownloadBusy}
                >
                  {t("downloadCsv")}
                </Button>
                    )}
              </div>
        
          </div>

          <div className="row mt-4">
            {canTime && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("timeOfDay")}
                </Typography>
                <Slider
                  value={timeRange}
                  onChange={(_, v) => setTimeRange(v)}
                  valueLabelDisplay="on"
                  min={0}
                  max={24}
                  marks={sliderMarks}
                  sx={{
                    mt: 3,
                    color: "var(--org-bg-color, #f6615f)",
                    "& .MuiSlider-valueLabel": {
                      backgroundColor: "#555",
                      color: "var(--org-font-color, #fff)",
                    },
                  }}
                />
              </div>
            )}

            {canEmployee && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("employee")}
                </Typography>
                <MultiSelect
                  label=""
                  options={employeeOptions}
                  value={employees}
                  onChange={(sel) => setEmployees(sel)}
                  onDelete={(v) =>
                    setEmployees((p) => p.filter((x) => x !== v))
                  }
                />
              </div>
            )}

            <div className="col-12 col-lg-4 d-flex flex-column mt-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showTraining}
                    onChange={() => setShowTraining((v) => !v)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t("training")}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showRead}
                    onChange={() => setShowRead((v) => !v)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t("read")}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showNull}
                    onChange={() => setShowNull((v) => !v)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t("see_null_data")}</Typography>}
              />
              {isGentofteLife ? (
                <>
                  <Typography variant="body2" fontWeight={600} gutterBottom sx={{ mt: 1.5 }}>
                    {t("incidentType")}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showIncidentYellow}
                        onChange={() => setShowIncidentYellow((v) => !v)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{t("yellow")}</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showIncidentRed}
                        onChange={() => setShowIncidentRed((v) => !v)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{t("red")}</Typography>}
                  />
                </>
              ) : null}
            </div>
          </div>
        </>
      );
    }

    if (activeTabKey === "reportingCategory") {
      return (
        <>
          <div className="row mt-4">
            {canDate && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("timePeriod")}
                </Typography>
                <div className="d-flex gap-2">
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={rDateFrom}
                    onChange={(e) => setRDateFrom(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={rDateTo}
                    onChange={(e) => setRDateTo(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                </div>
              </div>
            )}

            {canSet && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("setNumber")}
                </Typography>
                <MultiSelect
                  label=""
                  options={reportingCategorySetOptions}
                  value={rSetNumbers}
                  onChange={(sel) => setRSetNumbers(sel)}
                  onDelete={(value) =>
                    setRSetNumbers((current) => current.filter((item) => item !== value))
                  }
                />
              </div>
            )}

            {canDownload && (
              <div className="col-12 col-lg-4 d-flex flex-column gap-2 mt-1">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={csvDownloading ? <CircularProgress size={14} color="inherit" /> : <FaDownload />}
                  onClick={handleDownloadReportingCsv}
                  disabled={isRiskDownloadBusy}
                >
                  {t("downloadCsv")}
                </Button>
              </div>
            )}
          </div>

          <div className="row mt-4">
            {canTime && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("timeOfDay")}
                </Typography>
                <Slider
                  value={rTimeRange}
                  onChange={(_, value) => setRTimeRange(value)}
                  valueLabelDisplay="on"
                  min={0}
                  max={24}
                  marks={sliderMarks}
                  sx={{
                    mt: 3,
                    color: "var(--org-bg-color, #f6615f)",
                    "& .MuiSlider-valueLabel": {
                      backgroundColor: "#555",
                      color: "var(--org-font-color, #fff)",
                    },
                  }}
                />
              </div>
            )}

            {canEmployee && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("employee")}
                </Typography>
                <MultiSelect
                  label=""
                  options={employeeOptions}
                  value={rEmployees}
                  onChange={(sel) => setREmployees(sel)}
                  onDelete={(value) =>
                    setREmployees((current) => current.filter((item) => item !== value))
                  }
                />
              </div>
            )}

            <div className="col-12 col-lg-4 d-flex flex-column mt-1">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("reportingCategory")}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rShowNearMiss}
                    onChange={() => setRShowNearMiss((value) => !value)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t("nearMiss")}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rShowPrevention}
                    onChange={() => setRShowPrevention((value) => !value)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t("prevention")}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rShowDoubtAboutReporting}
                    onChange={() => setRShowDoubtAboutReporting((value) => !value)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t("doubtAboutReporting")}</Typography>}
              />
            </div>
          </div>
        </>
      );
    }

    if (activeTabKey === "performance") {
      return (
        <>
          <div className="row mt-4">
            {canDate && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("timePeriod")}
                </Typography>
                <div className="d-flex gap-2">
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                </div>
              </div>
            )}

            <div className="col-12 col-lg-4 d-flex flex-column gap-2 mt-1">
              <Button
                variant="outlined"
                size="small"
                startIcon={pdfDownloading ? <CircularProgress size={14} color="inherit" /> : <FaDownload />}
                onClick={handleDownloadPdf}
                disabled={isRiskDownloadBusy}
              >
                {t("downloadPdf")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={pngDownloading ? <CircularProgress size={14} color="inherit" /> : <FaDownload />}
                onClick={handleDownloadPng}
                disabled={isRiskDownloadBusy}
              >
                {t("downloadPng")}
              </Button>
              {canDownload && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={csvDownloading ? <CircularProgress size={14} color="inherit" /> : <FaDownload />}
                  onClick={handleDownloadRiskCsv}
                  disabled={isRiskDownloadBusy}
                >
                  {t("downloadCsv")}
                </Button>
                    )}
            </div>
          </div>

          <div className="row mt-4">
            {canTime && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t("timeOfDay")}
                </Typography>
                <Slider
                  value={timeRange}
                  onChange={(_, v) => setTimeRange(v)}
                  valueLabelDisplay="on"
                  min={0}
                  max={24}
                  marks={sliderMarks}
                  sx={{
                    mt: 3,
                    color: "var(--org-bg-color, #f6615f)",
                    "& .MuiSlider-valueLabel": {
                      backgroundColor: "#555",
                      color: "var(--org-font-color, #fff)",
                    },
                  }}
                />
              </div>
            )}
          </div>
        </>
      );
    }

    // Mission filters
    if (activeTabKey === "mission") {
      return (
        <>
          <div className="row g-3 mt-3">
            {canDate && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("timePeriod")}
                </Typography>
                <div className="d-flex gap-2">
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={mDateFrom}
                    onChange={(e) => setMDateFrom(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={mDateTo}
                    onChange={(e) => setMDateTo(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                </div>
              </div>
            )}

            {canSet && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("setNumber")}
                </Typography>
                <MultiSelect
                  label=""
                  options={missionSetOptions}
                  value={mSetNumbers}
                  onChange={(sel) => setMSetNumbers(sel)}
                  onDelete={(v) =>
                    setMSetNumbers((p) => p.filter((x) => x !== v))
                  }
                />
              </div>
            )}

            {canDownload && (
              <div className="col-12 col-lg-4 d-flex flex-column gap-2 mt-1">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FaDownload />}
                  onClick={() => downloadCsv(missionFiltered, "mission.csv")}
                >
                  {t("downloadCsv")}
                </Button>
              </div>
            )}
          </div>

          <div className="row g-3 mt-2">
            {canTime && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("timeOfDay")}
                </Typography>
                <Slider
                  value={mTimeRange}
                  onChange={(_, v) => setMTimeRange(v)}
                  valueLabelDisplay="on"
                  min={0}
                  max={24}
                  marks={sliderMarks}
                  sx={{
                    mt: 3,
                    color: "var(--org-bg-color, #f6615f)",
                    "& .MuiSlider-valueLabel": {
                      backgroundColor: "#555",
                      color: "var(--org-font-color, #fff)",
                    },
                  }}
                />
              </div>
            )}
            <div className="col-12 col-lg-3 d-flex flex-column">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={mShowGreen}
                    onChange={() => setMShowGreen((v) => !v)}
                    size="small"
                    sx={{
                      color: "#5a8a2e",
                      "&.Mui-checked": { color: "#5a8a2e" },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="#5a8a2e">
                    Green
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={mShowYellow}
                    onChange={() => setMShowYellow((v) => !v)}
                    size="small"
                    sx={{
                      color: "#e6a817",
                      "&.Mui-checked": { color: "#e6a817" },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="#e6a817">
                    Yellow
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={mShowRed}
                    onChange={() => setMShowRed((v) => !v)}
                    size="small"
                    sx={{
                      color: "#c0392b",
                      "&.Mui-checked": { color: "#c0392b" },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="#c0392b">
                    Red
                  </Typography>
                }
              />
            </div>
          </div>
        </>
      );
    }

    // Guest filters
    if (activeTabKey === "guest") {
      return (
        <>
          <div className="row g-3 mt-3">
            {canDate && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("timePeriod")}
                </Typography>
                <div className="d-flex gap-2">
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={gDateFrom}
                    onChange={(e) => setGDateFrom(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={gDateTo}
                    onChange={(e) => setGDateTo(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                </div>
              </div>
            )}

            {canSet && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("setNumber")}
                </Typography>
                <MultiSelect
                  label=""
                  options={missionSetOptions}
                  value={gSetNumbers}
                  onChange={(sel) => setGSetNumbers(sel)}
                  onDelete={(v) =>
                    setGSetNumbers((p) => p.filter((x) => x !== v))
                  }
                />
              </div>
            )}

            {canDownload && (
              <div className="col-12 col-lg-4 d-flex flex-column gap-2 mt-1">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FaDownload />}
                  onClick={() => downloadCsv(guestFiltered, "guest.csv")}
                >
                  {t("downloadCsv")}
                </Button>
              </div>
            )}
          </div>

          <div className="row g-3 mt-2">
            {canTime && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("timeOfDay")}
                </Typography>
                <Slider
                  value={gTimeRange}
                  onChange={(_, v) => setGTimeRange(v)}
                  valueLabelDisplay="on"
                  min={0}
                  max={24}
                  marks={sliderMarks}
                  sx={{
                    mt: 3,
                    color: "var(--org-bg-color, #f6615f)",
                    "& .MuiSlider-valueLabel": {
                      backgroundColor: "#555",
                      color: "var(--org-font-color, #fff)",
                    },
                  }}
                />
              </div>
            )}
          </div>
        </>
      );
    }

    // Estimated filters
    if (activeTabKey === "estimated") {
      return (
        <>
          <div className="row g-3 mt-3">
            {canDate && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("timePeriod")}
                </Typography>
                <div className="d-flex gap-2">
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={eDateFrom}
                    onChange={(e) => setEDateFrom(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={eDateTo}
                    onChange={(e) => setEDateTo(e.target.value)}
                    inputProps={{ min: MIN_DATE, max: todayStr() }}
                  />
                </div>
              </div>
            )}

            {canSet && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("setNumber")}
                </Typography>
                <MultiSelect
                  label=""
                  options={estimatedSetOptions}
                  value={eSetNumbers}
                  onChange={(sel) => setESetNumbers(sel)}
                  onDelete={(v) =>
                    setESetNumbers((p) => p.filter((x) => x !== v))
                  }
                />
              </div>
            )}

            {canDownload && (
              <div className="col-12 col-lg-4 d-flex flex-column gap-2 mt-1">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FaDownload />}
                  onClick={() => downloadCsv(estimatedFiltered, "estimated.csv")}
                >
                  {t("downloadCsv")}
                </Button>
              </div>
            )}
          </div>

          <div className="row g-3 mt-2">
            {canTime && (
              <div className="col-12 col-lg-4">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {t("timeOfDay")}
                </Typography>
                <Slider
                  value={eTimeRange}
                  onChange={(_, v) => setETimeRange(v)}
                  valueLabelDisplay="on"
                  min={0}
                  max={24}
                  marks={sliderMarks}
                  sx={{
                    mt: 3,
                    color: "var(--org-bg-color, #f6615f)",
                    "& .MuiSlider-valueLabel": {
                      backgroundColor: "#555",
                      color: "var(--org-font-color, #fff)",
                    },
                  }}
                />
              </div>
            )}
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {t("beaches")}
              </Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={eBeach}
                onChange={(e) => setEBeach(e.target.value)}
                inputProps={{ style: { fontSize: 13 } }}
              >
                <MenuItem value="">
                  <em>Alle strande</em>
                </MenuItem>
                {Array.from(
                  new Set((estimatedData ?? []).map((r) => r.beach).filter(Boolean))
                )
                  .sort()
                  .map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
              </TextField>
            </div>
          </div>
        </>
      );
    }

    return null;
  };

  // ── Build tab list dynamically based on org ──────────────────────────────
  const tabDefs = useMemo(() => {
    const tabs = [
      { key: "faktuel", label: t("faktuel") },
    ];
    if (showReportingCategory) {
      tabs.push({ key: "reportingCategory", label: t("reportingCategory") });
    }
    tabs.push({ key: "performance", label: t("performance") });
    if (showMissionGuest(organizationId)) {
      tabs.push({ key: "mission", label: t("mission") });
      tabs.push({ key: "guest", label: t("guest") });
    }
    if (showEstimated(organizationId)) {
      tabs.push({ key: "estimated", label: t("estimated") });
    }
    return tabs;
  }, [lang, organizationId, showReportingCategory]);

  useEffect(() => {
    if (tab >= tabDefs.length) {
      setTab(0);
    }
  }, [tab, tabDefs]);

  const activeTabKey = tabDefs[tab]?.key;

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 3 }}>
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography style={{fontWeight:"bold", fontSize:"30px"}}>
            {orgLoading ? "Loading..." : orgName}
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            {["dk", "en"].map((l) => (
              <FormControlLabel
                key={l}
                control={
                  <input
                    type="radio"
                    checked={lang === l}
                    onChange={() => setLang(l)}
                    style={{ marginRight: 4 }}
                  />
                }
                label={l === "dk" ? "Danish" : "English"}
              />
            ))}
          </Box>
        </Box>

        <hr style={{ color: "red" }} />

        {/* ── Filters above tabs for all tabs ── */}
        {renderFiltersForTab()}
        <hr style={{ color: "red" }} />

        {/* ── Tabs ── */}
        <Box sx={{ mt: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 48,
              p: 0.75,
              borderRadius: 3,
              backgroundColor: "#fff5f5",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                textTransform: "none",
                borderRadius: 2.5,
                minHeight: 40,
                color: "#8b3a3a",
                fontWeight: 600,
                transition: "all 0.2s ease",
              },
              "& .Mui-selected": {
                backgroundColor: "#fde5e5",
                color: "#a33232",
              },
            }}
          >
            {tabDefs.map((td) => (
              <Tab key={td.key} label={td.label} />
            ))}
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {/* Tab 0 — Faktuel */}
            {activeTabKey === "faktuel" && (
              <>
                <div ref={incidentRef}>
                  {prototypeChartGroups.map((group, index) => (
                    <Box key={`incident-${group.key}`} sx={{ mb: index === prototypeChartGroups.length - 1 ? 0 : 4 }}>
                      {isPrototypeGrouped && group.title ? (
                        <Typography sx={{ mb: 2, fontWeight: 700, color: "#8b3a3a" }}>
                          {group.title}
                        </Typography>
                      ) : null}
                      <div
                        ref={(node) => assignNodeRef(incidentChartRefs, group.key, node)}
                        style={{ width: "100%" }}
                      >
                        <RiskBarChart
                          defaultRecords={group.defaultRecords}
                          reportingCategoryRecords={[]}
                          trainingRecords={group.trainingRecords}
                          readRecords={group.readRecords}
                          organizationId={organizationId}
                          showIncidentYellow={showIncidentYellow}
                          showIncidentRed={showIncidentRed}
                          showTraining={showTraining}
                          showRead={showRead}
                          actionCards={group.actionCards}
                          lang={lang}
                        />
                      </div>
                    </Box>
                  ))}
                </div>
                <hr style={{ color: "red" }} />
                <div ref={tableRef}>
                  {riskRecordsLoading || actionCardsLoading
                    ? renderLoader(lang === "dk" ? "Indlæser rådata..." : "Loading raw records...")
                    : <RawRecordsTable records={displayTableRecords} actionCards={actionCards} lang={lang} />}
                </div>
            </>
          )}

            {activeTabKey === "reportingCategory" && (
              <>
                {riskRecordsLoading || actionCardsLoading ? (
                  renderLoader(
                    lang === "dk"
                      ? "Indlæser rapporteringskategorier..."
                      : "Loading reporting categories..."
                  )
                ) : (
                  <>
                    <div>
                      {reportingPrototypeGroups.map((group, index) => (
                        <Box
                          key={`reporting-${group.key}`}
                          sx={{ mb: index === reportingPrototypeGroups.length - 1 ? 0 : 4 }}
                        >
                          {isPrototypeGrouped && group.title ? (
                            <Typography sx={{ mb: 2, fontWeight: 700, color: "#8b3a3a" }}>
                              {group.title}
                            </Typography>
                          ) : null}
                          <div style={{ width: "100%" }}>
                            <ReportingCategoryDateChart
                              records={group.reportingCategoryRecords}
                              dateFrom={rDateFrom}
                              dateTo={rDateTo}
                              showNearMiss={rShowNearMiss}
                              showPrevention={rShowPrevention}
                              showDoubtAboutReporting={rShowDoubtAboutReporting}
                              lang={lang}
                            />
                          </div>
                        </Box>
                      ))}
                    </div>
                    <hr style={{ color: "red" }} />
                    <div>
                      <ReportingCategoryTable
                        records={displayReportingTableRecords}
                        actionCards={actionCards}
                        lang={lang}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {/* Tab 1 — T-performance */}
            {activeTabKey === "performance" && (
              <>
                <div ref={tPerformanceRef}>
                  {prototypeChartGroups.map((group, index) => (
                    <Box key={`performance-${group.key}`} sx={{ mb: index === prototypeChartGroups.length - 1 ? 0 : 4 }}>
                      {isPrototypeGrouped && group.title ? (
                        <Typography sx={{ mb: 2, fontWeight: 700, color: "#8b3a3a" }}>
                          {group.title}
                        </Typography>
                      ) : null}
                      <div
                        ref={(node) => assignNodeRef(tPerformanceChartRefs, group.key, node)}
                        style={{ width: "100%" }}
                      >
                        <TPerformanceChart
                          records={group.performanceRecords}
                          actionCards={group.actionCards}
                          lang={lang}
                        />
                      </div>
                    </Box>
                  ))}
                </div>
                <hr style={{ color: "red" }} />
              </>
            )}

            {/* Tab 2 — Mission (org 19 & 2) */}
            {activeTabKey === "mission" && (
              missionGuestLoading ? (
                renderLoader("Loading mission & guest data...")
              ) : missionGuestError ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {String(missionGuestError)}
                </Alert>
              ) : (
                <div ref={missionRef}>
                  <MissionChart
                    data={missionFiltered}
                    organizationId={organizationId}
                    lang={lang}
                    hideFilters
                    externalFilters={{
                      dateFrom: mDateFrom,
                      dateTo: mDateTo,
                      timeRange: mTimeRange,
                      setNumbers: mSetNumbers,
                      showGreen: mShowGreen,
                      showYellow: mShowYellow,
                      showRed: mShowRed,
                      setDateFrom: setMDateFrom,
                      setDateTo: setMDateTo,
                      setTimeRange: setMTimeRange,
                      setSetNumbers: setMSetNumbers,
                      setShowGreen: setMShowGreen,
                      setShowYellow: setMShowYellow,
                      setShowRed: setMShowRed,
                    }}
                  />
                </div>
              )
            )}

            {/* Tab 3 — Guest (org 19 & 2) */}
            {activeTabKey === "guest" && (
              missionGuestLoading ? (
                renderLoader("Loading mission & guest data...")
              ) : missionGuestError ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {String(missionGuestError)}
                </Alert>
              ) : (
                <div ref={guestRef}>
                  <GuestChart
                    data={guestFiltered}
                    organizationId={organizationId}
                    lang={lang}
                    hideFilters
                    externalFilters={{
                      dateFrom: gDateFrom,
                      dateTo: gDateTo,
                      timeRange: gTimeRange,
                      setNumbers: gSetNumbers,
                      setDateFrom: setGDateFrom,
                      setDateTo: setGDateTo,
                      setTimeRange: setGTimeRange,
                      setSetNumbers: setGSetNumbers,
                    }}
                  />
                </div>
              )
            )}

            {/* Tab 4 — Estimated time (org 19 only) */}
            {activeTabKey === "estimated" && (
              estimatedLoading ? (
                renderLoader("Loading estimated time data...")
              ) : estimatedError ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {String(estimatedError)}
                </Alert>
              ) : (
                <div ref={estimatedRef}>
                  <EstimatedTimeChart
                    data={estimatedFiltered}
                    organizationId={organizationId}
                    lang={lang}
                    hideFilters
                    externalFilters={{
                      dateFrom: eDateFrom,
                      dateTo: eDateTo,
                      timeRange: eTimeRange,
                      setNumbers: eSetNumbers,
                      beach: eBeach,
                      setDateFrom: setEDateFrom,
                      setDateTo: setEDateTo,
                      setTimeRange: setETimeRange,
                      setSetNumbers: setESetNumbers,
                      setBeach: setEBeach,
                    }}
                  />
                </div>
              )
            )}
          </Box>
        </Box>
      </Box>
      {/* Hidden off-screen composed cards for PNG zip export */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          top: 0,
          transform: "translateX(-240vw)",
          pointerEvents: "none",
          opacity: 0,
          zIndex: -1,
        }}
      >
        {prototypeChartGroups.map((group) => {
          const riskSubtitle = [
            lang === "dk" ? "Faktisk hændelse" : "Real Incident",
            isPrototypeGrouped ? group.title : "",
          ]
            .filter(Boolean)
            .join(" - ");
          const performanceSubtitle = [
            "T-Performance",
            isPrototypeGrouped ? group.title : "",
          ]
            .filter(Boolean)
            .join(" - ");

          return (
            <Box key={`image-export-${group.key}`}>
              <div
                ref={(node) => assignNodeRef(incidentImageExportRefs, group.key, node)}
                style={{ background: "#fff" }}
              >
                <ChartImageExportCard
                  title="Action-cards® til sikker førstehjælp"
                  subtitle={riskSubtitle}
                  headerRows={buildImageHeaderRows()}
                  detailRows={buildRiskImageDetailRows()}
                >
                  <RiskBarChart
                    defaultRecords={group.defaultRecords}
                    reportingCategoryRecords={[]}
                    trainingRecords={group.trainingRecords}
                    readRecords={group.readRecords}
                    organizationId={organizationId}
                    showIncidentYellow={showIncidentYellow}
                    showIncidentRed={showIncidentRed}
                    showTraining={showTraining}
                    showRead={showRead}
                    actionCards={group.actionCards}
                    lang={lang}
                    height={860}
                  />
                </ChartImageExportCard>
              </div>

              <div
                ref={(node) => assignNodeRef(tPerformanceImageExportRefs, group.key, node)}
                style={{ background: "#fff" }}
              >
                <ChartImageExportCard
                  title="Action-cards® til sikker førstehjælp"
                  subtitle={performanceSubtitle}
                  headerRows={buildImageHeaderRows()}
                  detailRows={buildPerformanceImageDetailRows()}
                >
                  <TPerformanceChart
                    records={group.performanceRecords}
                    actionCards={group.actionCards}
                    lang={lang}
                    height={860}
                  />
                </ChartImageExportCard>
              </div>
            </Box>
          );
        })}
      </Box>

      {/* Off-screen copies of charts for crisp PDF export (no HTML→PNG screenshots) */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          top: 0,
          transform: "translateX(-200vw)",
          width: "1400px",
          pointerEvents: "none",
          opacity: 0,
          background: "#fff",
          zIndex: -1,
        }}
      >
        {prototypeChartGroups.map((group) => (
          <div key={`incident-export-${group.key}`}>
            <div
              ref={(node) => assignNodeRef(incidentExportRefs, group.key, node)}
              style={{ width: "1600px", height: "920px" }}
            >
              <RiskBarChart
                defaultRecords={group.defaultRecords}
                reportingCategoryRecords={[]}
                trainingRecords={group.trainingRecords}
                readRecords={group.readRecords}
                organizationId={organizationId}
                showIncidentYellow={showIncidentYellow}
                showIncidentRed={showIncidentRed}
                showTraining={showTraining}
                showRead={showRead}
                actionCards={group.actionCards}
                lang={lang}
                height={900}
              />
            </div>

            <div
              ref={(node) => assignNodeRef(tPerformanceExportRefs, group.key, node)}
              style={{ width: "1600px", height: "920px" }}
            >
              <TPerformanceChart
                records={group.performanceRecords}
                actionCards={group.actionCards}
                lang={lang}
                height={900}
              />
            </div>
          </div>
        ))}

        {showMissionGuest(organizationId) && (
          <div ref={missionExportRef} style={{ width: "1400px", height: "430px" }}>
            <MissionChart
              data={missionFiltered}
              organizationId={organizationId}
              lang={lang}
              hideFilters
              externalFilters={{
                dateFrom: mDateFrom,
                dateTo: mDateTo,
                timeRange: mTimeRange,
                setNumbers: mSetNumbers,
                showGreen: mShowGreen,
                showYellow: mShowYellow,
                showRed: mShowRed,
                setDateFrom: setMDateFrom,
                setDateTo: setMDateTo,
                setTimeRange: setMTimeRange,
                setSetNumbers: setMSetNumbers,
                setShowGreen: setMShowGreen,
                setShowYellow: setMShowYellow,
                setShowRed: setMShowRed,
              }}
            />
          </div>
        )}

        {showMissionGuest(organizationId) && (
          <div ref={guestExportRef} style={{ width: "1400px", height: "430px" }}>
            <GuestChart
              data={guestFiltered}
              organizationId={organizationId}
              lang={lang}
              hideFilters
              externalFilters={{
                dateFrom: gDateFrom,
                dateTo: gDateTo,
                timeRange: gTimeRange,
                setNumbers: gSetNumbers,
                setDateFrom: setGDateFrom,
                setDateTo: setGDateTo,
                setTimeRange: setGTimeRange,
                setSetNumbers: setGSetNumbers,
              }}
            />
          </div>
        )}

        {showEstimated(organizationId) && (
          <div ref={estimatedExportRef} style={{ width: "1400px", height: "430px" }}>
            <EstimatedTimeChart
              data={estimatedFiltered}
              organizationId={organizationId}
              lang={lang}
              hideFilters
              externalFilters={{
                dateFrom: eDateFrom,
                dateTo: eDateTo,
                timeRange: eTimeRange,
                setNumbers: eSetNumbers,
                beach: eBeach,
                setDateFrom: setEDateFrom,
                setDateTo: setEDateTo,
                setTimeRange: setETimeRange,
                setSetNumbers: setESetNumbers,
                setBeach: setEBeach,
              }}
            />
          </div>
        )}
      </Box>
    </Box>
  );
};

export default RiskAnalysis;
